/**
 * Validates CompareSlider component props
 * @param {object} props - The props object to validate
 * @param {boolean} [throwOnError=false] - If true, throws an error on validation failure. If false, returns validation result
 * @return {{isValid: boolean, errors: string[]}} ValidationResult object containing isValid flag and array of error messages
 * @throws {Error} if throwOnError is true and validation fails
 */
export const validateCompareSliderProps = (props, throwOnError = false) => {
  const errors = []

  // Check if props is an object
  if (!props || typeof props !== "object") {
    errors.push("Props must be an object")
    if (throwOnError) {
      throw new Error(`CompareSlider validation failed:\n${errors.join("\n")}`)
    }
    return { isValid: false, errors }
  }

  // Validate required heading
  if (!props.heading) {
    errors.push("heading is required")
  } else {
    if (typeof props.heading !== "object") {
      errors.push("heading must be an object")
    } else {
      if (!props.heading.text) {
        errors.push("heading.text is required")
      }
      if (
        typeof props.heading.text !== "string" &&
        typeof props.heading.text !== "object"
      ) {
        errors.push("heading.text must be a string or React element")
      }
    }
  }

  // Validate required cards
  if (!props.cards) {
    errors.push("cards is required")
  } else if (!Array.isArray(props.cards)) {
    errors.push("cards must be an array")
  } else if (props.cards.length === 0) {
    errors.push("cards array must not be empty")
  } else {
    props.cards.forEach((card, index) => {
      if (typeof card !== "object" || card === null) {
        errors.push(`cards[${index}] must be an object`)
      } else {
        // Validate required image
        if (!card.image) {
          errors.push(`cards[${index}].image is required`)
        } else {
          if (typeof card.image !== "object") {
            errors.push(`cards[${index}].image must be an object`)
          } else {
            if (!card.image.src) {
              errors.push(`cards[${index}].image.src is required`)
            }
          }
        }

        // Validate required title
        if (!card.title) {
          errors.push(`cards[${index}].title is required`)
        } else {
          if (typeof card.title !== "object") {
            errors.push(`cards[${index}].title must be an object`)
          } else {
            if (!card.title.text) {
              errors.push(`cards[${index}].title.text is required`)
            }
          }
        }

        // Validate optional description
        if (card.description !== undefined) {
          if (
            typeof card.description !== "object" ||
            card.description === null
          ) {
            errors.push(
              `cards[${index}].description must be an object if provided`
            )
          } else {
            if (!card.description.text) {
              errors.push(
                `cards[${index}].description.text is required when description is provided`
              )
            }
          }
        }

        // Validate optional price
        if (card.price !== undefined) {
          if (typeof card.price !== "object" || card.price === null) {
            errors.push(`cards[${index}].price must be an object if provided`)
          } else {
            if (!card.price.text) {
              errors.push(
                `cards[${index}].price.text is required when price is provided`
              )
            }
          }
        }

        // Validate optional features
        if (card.features !== undefined) {
          if (!Array.isArray(card.features)) {
            errors.push(`cards[${index}].features must be an array if provided`)
          } else {
            card.features.forEach((feature, featureIndex) => {
              if (typeof feature !== "object" || feature === null) {
                errors.push(
                  `cards[${index}].features[${featureIndex}] must be an object`
                )
              } else {
                if (!feature.text) {
                  errors.push(
                    `cards[${index}].features[${featureIndex}].text is required`
                  )
                }
              }
            })
          }
        }

        // Validate optional CTA
        if (card.CTA !== undefined) {
          if (typeof card.CTA !== "object" || card.CTA === null) {
            errors.push(`cards[${index}].CTA must be an object if provided`)
          } else {
            if (!card.CTA.text) {
              errors.push(
                `cards[${index}].CTA.text is required when CTA is provided`
              )
            }
            if (!card.CTA.href && !card.CTA.onClick) {
              errors.push(
                `cards[${index}].CTA must have either href or onClick`
              )
            }
          }
        }
      }
    })
  }

  // Validate optional CTA
  if (props.CTA !== undefined) {
    if (typeof props.CTA !== "object" || props.CTA === null) {
      errors.push("CTA must be an object if provided")
    } else {
      if (!props.CTA.text) {
        errors.push("CTA.text is required when CTA is provided")
      }
      if (!props.CTA.href && !props.CTA.onClick) {
        errors.push("CTA must have either href or onClick")
      }
    }
  }

  // Validate optional topSpacer
  if (props.topSpacer !== undefined) {
    if (typeof props.topSpacer !== "object" || props.topSpacer === null) {
      errors.push("topSpacer must be an object if provided")
    }
  }

  // Validate optional bottomSpacer
  if (props.bottomSpacer !== undefined) {
    if (typeof props.bottomSpacer !== "object" || props.bottomSpacer === null) {
      errors.push("bottomSpacer must be an object if provided")
    }
  }

  const isValid = errors.length === 0

  if (!isValid && throwOnError) {
    throw new Error(`CompareSlider validation failed:\n${errors.join("\n")}`)
  }

  return { isValid, errors }
}
